import React, { useState, useContext } from 'react';
import { Button, Input, Modal } from '../common';
import { ToastContext } from '../../context/ToastContext';

export const SceneAspects = ({
  aspects = [],
  canAdd = false,
  canRemove = false,
  onAdd,
  onRemove,
  influenceCost = 1,
  isGM = false,
  balance = 0,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAspect, setNewAspect] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const toast = useContext(ToastContext);

  const canAfford = isGM || balance >= influenceCost;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newAspect.name.trim()) return;
    if (!isGM && !canAfford) return;

    setLoading(true);
    try {
      await onAdd(newAspect);
      setNewAspect({ name: '', description: '' });
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to add aspect:', error);
      toast?.error('Failed to add aspect');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (confirmDeleteIndex === null) return;

    setDeleting(true);
    try {
      await onRemove(confirmDeleteIndex);
      setConfirmDeleteIndex(null);
    } catch (error) {
      console.error('Failed to remove aspect:', error);
      // Show appropriate error message
      const message = error.response?.data?.error || 'Failed to remove aspect';
      toast?.error(message);
      setConfirmDeleteIndex(null);
    } finally {
      setDeleting(false);
    }
  };

  const aspectToDelete = confirmDeleteIndex !== null ? aspects[confirmDeleteIndex] : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">Aspects</h4>
        {canAdd && (
          <button
            onClick={() => setShowAddModal(true)}
            className="text-xs text-primary-600 hover:text-primary-700"
          >
            + Add
          </button>
        )}
      </div>

      {aspects.length === 0 && (
        <p className="text-gray-500 text-sm italic">No aspects defined yet.</p>
      )}

      <ul className="space-y-2">
        {aspects.map((aspect, index) => (
          <li
            key={index}
            className="flex items-start justify-between bg-gray-50 rounded-lg p-3"
          >
            <div>
              <span className="font-medium text-gray-900">"{aspect.name}"</span>
              {aspect.description && (
                <p className="text-sm text-gray-600 mt-1">{aspect.description}</p>
              )}
            </div>
            {canRemove && (
              <button
                onClick={() => setConfirmDeleteIndex(index)}
                className="text-gray-400 hover:text-red-500 ml-2"
                title="Remove aspect"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </li>
        ))}
      </ul>

      {/* Add Aspect Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewAspect({ name: '', description: '' });
        }}
        title="Add Aspect"
        size="sm"
      >
        <form onSubmit={handleAdd} className="space-y-4">
          <Input
            label="Aspect Name"
            value={newAspect.name}
            onChange={(e) => setNewAspect({ ...newAspect, name: e.target.value })}
            placeholder='e.g., "Tension in the Air"'
            maxLength={100}
            required
          />
          <Input
            label="Description (optional)"
            value={newAspect.description}
            onChange={(e) => setNewAspect({ ...newAspect, description: e.target.value })}
            placeholder="Brief description of this aspect"
            maxLength={500}
          />
          {!isGM && (
            <p className={`text-sm ${canAfford ? 'text-gray-600' : 'text-red-600'}`}>
              {canAfford
                ? `This will cost ${influenceCost} influence (you have ${balance}).`
                : `Not enough influence. You need ${influenceCost} but have ${balance}.`
              }
            </p>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAddModal(false);
                setNewAspect({ name: '', description: '' });
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!isGM && !canAfford}
            >
              Add Aspect
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={confirmDeleteIndex !== null}
        onClose={() => setConfirmDeleteIndex(null)}
        title="Remove Aspect"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to remove this aspect?
          </p>
          {aspectToDelete && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-medium text-gray-900">"{aspectToDelete.name}"</p>
              {aspectToDelete.description && (
                <p className="text-sm text-gray-600 mt-1">{aspectToDelete.description}</p>
              )}
            </div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <Button
              variant="secondary"
              onClick={() => setConfirmDeleteIndex(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleRemove}
              loading={deleting}
            >
              Remove
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
